import { IAppContext } from 'App/AppContext';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from './localstorage';

// authSchemaId 0 is not a valid schema id, so it is used as an explicit marker for a
// deliberate "Everyone" choice, distinct from nothing being remembered yet.
const everyoneMarker = 0;

/**
 * Returns the auth schema id the current user last used to share a task, to be used as the
 * default for newly created tasks. Returns undefined (meaning "Everyone") when nothing is
 * remembered yet, when the user last deliberately chose "Everyone", or when the remembered
 * schema no longer exists or is no longer active, and when the user has no access to permission sets.
 */
export function getLastUsedTaskAuthSchemaId(appContext: IAppContext): number | undefined {
  if (!hasUserFeature(appContext, FeatureTypes.RBAC)) return undefined;

  const stored = getLocalStorageData(appContext, LocalStorageKeys.TaskLastAuthSchemaId);
  if (!stored) return undefined;

  const authSchemaId = parseInt(stored, 10);
  if (isNaN(authSchemaId) || authSchemaId === everyoneMarker) return undefined;

  if (!appContext.globalDataCache.authSchemas.has(authSchemaId)) return undefined;
  if (!appContext.globalDataCache.authSchemas.get(authSchemaId).active) return undefined;

  return authSchemaId;
}

/**
 * Remembers the auth schema id the user chose for a task, so it can be used as the default
 * the next time a task is created. A deliberate "Everyone" choice (undefined) is stored using
 * an explicit marker, so it is remembered just like any other choice.
 */
export function rememberTaskAuthSchemaId(appContext: IAppContext, authSchemaId: number | undefined) {
  setLocalStorageData(appContext, LocalStorageKeys.TaskLastAuthSchemaId, String(authSchemaId ?? everyoneMarker));
}
